import env from '@shared/env'
import { getOneTimeAuthToken } from '@shared/api-endpoints'
import useAuthStore from '@shared/stores/useAuthStore.js'
import { semVerCompare } from '@shared/utils.js'
import useSharedStore from '@shared/stores/useSharedStore.js'

/**
 * Assemble a deeplink for the Kaia Apps
 * @param [path='open'] {string}
 * @param [query={}] {Object.<string, string | number | boolean>} the key <> value pairs of the query, whereas the values will be casted to string and URI encoded
 * @param [addOneTimeAuthToken=false] {boolean} if true loads a oneTimeAuthToken from the backend and adds it to the query. IMPORTANT: if that fails it does not return the normal requested deeplink but instead just returns a simple deeplink that opens the app. Additionally, in case a token can be added it will (force) use URI scheme
 * @param [fallback] {function} a function that returns a fallback deeplink. This will be called when the oneTimeAuthToken could not be appended to the deeplink. If that function is not defined an error will be thrown in that case.
 * @throws when `addOneTimeAuthToken` is true but appending the token failed (eg due to api request failing or response being malformed)
 * @returns {Promise<string>}
 */
const assembleKaiaAppDeepLink = async ({
  path = 'open',
  query = {},
  addOneTimeAuthToken = false,
  fallback,
}) => {
  if (addOneTimeAuthToken) {
    try {
      const sharedStore = useSharedStore()
      const getTokenResponse = await getOneTimeAuthToken(
        sharedStore.initialQuery.code_challenge,
      )
      const token = getTokenResponse?.data?.token
      if (!token?.length > 0) throw 'no valid one_time_token'
      query.token = token
    } catch (error) {
      if (fallback instanceof Function) {
        console.warn(
          'failed to append oneTimeToken to deeplink, using fallback deeplink',
          error,
        )
        return fallback()
      }
      throw error
    }
  }

  let scheme = env.VITE_KAIA_APP_DEEP_LINK

  const authStore = useAuthStore()
  // app versions before 2.153.0 don't support universal links for assessment
  // completion yet, so we have to use uri schemes for these old app versions
  if (semVerCompare(authStore.hostClientVersion, '2.153.0') < 0) {
    const schemes = {
      msk: {
        us: 'kaiahealth://',
        eu: 'kaiahealth-msk-eu://',
      },
      copd: {
        us: 'kaiahealth-breathe-us://',
        eu: 'kaiahealth-copd://',
      },
    }
    scheme = schemes[env.VITE_DISEASE][env.VITE_GEO]
  }

  const url = new URL(scheme + path)

  // URL.searchParams.append will automatically url encode the values
  Object.entries(query).forEach(([key, value]) =>
    url.searchParams.append(key, value.toString()),
  )

  return url.toString()
}

export default assembleKaiaAppDeepLink
